import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useAISearchProgress from '../../../api/useAISearchProgress';
import {
    identifiedListDefaultPageSize,
    IdentifiedProfileOptions,
    useIdentifiedProfiles,
} from '../../../api/useIdentifiedProfiles';
import {
    convertSearchCriteriaResponseToSearchCriteriaFilters,
    DefaultRefineSearchCriteria,
    RefineSearchCriteriaFormValues,
    useSearchCriteria,
} from '../../../api/useSearchCriteria';
import {
    convertSearchFiltersToIdentifiedFilters,
    DefaultRefineSearchFilters,
    RefineSearchFilterFormValues,
    useSearchFilters,
} from '../../../api/useSearchFilters';
import { usePagination, useRecruitmentId } from '@/Hooks';
import { Pagination } from '@/Models/Page';

export const minLocationRadius: number = 1;
export const minYearsOfExperience: number = 0;
export const maxYearsOfExperience: number = 30;

export interface IdentifiedListContextType extends ReturnType<typeof useIdentifiedProfiles> {
    pagination: Pagination;
    setPagination: React.Dispatch<React.SetStateAction<Pagination>>;
    refineSearchFilters: RefineSearchFilterFormValues;
    setRefineSearchFilters: React.Dispatch<React.SetStateAction<RefineSearchFilterFormValues>>;
    refineSearchCriteria: RefineSearchCriteriaFormValues;
    setRefineSearchCriteria: React.Dispatch<React.SetStateAction<RefineSearchCriteriaFormValues>>;
    isRefineSearchFiltersReady: boolean;
    isRefineSearchCriteriaReady: boolean;
    options: IdentifiedProfileOptions;
    setOptions: React.Dispatch<React.SetStateAction<IdentifiedProfileOptions>>;
}

export const IdentifiedListContext = createContext<IdentifiedListContextType>(undefined!);

export const useIdentifiedListContextProvider = (): IdentifiedListContextType => {
    const recruitmentId = useRecruitmentId();
    const [options, setOptionsState] = useState<IdentifiedProfileOptions>({});
    const [refineSearchFiltersOverrides, setRefineSearchFiltersOverrides] =
        useState<RefineSearchFilterFormValues | null>(null);
    const [refineSearchCriteriaOverrides, setRefineSearchCriteriaOverrides] =
        useState<RefineSearchCriteriaFormValues | null>(null);
    const [pagination, setPagination] = usePagination(identifiedListDefaultPageSize);

    const identifiedProfiles = useIdentifiedProfiles(pagination.limit, pagination.offset, options);
    const { data: progressData, isLoading: isProgressDataLoading } = useAISearchProgress();
    const { data: searchFiltersData, isLoading: isSearchFiltersLoading } = useSearchFilters(recruitmentId!);
    const { data: searchCriteriaData, isLoading: isSearchCriteriaLoading } = useSearchCriteria(recruitmentId!);

    const isAiSearching = isProgressDataLoading || progressData?.isInProgress;

    const baseRefineSearchFilters = useMemo((): RefineSearchFilterFormValues => {
        if (!searchFiltersData) {
            return DefaultRefineSearchFilters;
        }

        return convertSearchFiltersToIdentifiedFilters(searchFiltersData);
    }, [searchFiltersData]);

    const baseRefineSearchCriteria = useMemo((): RefineSearchCriteriaFormValues => {
        if (!searchCriteriaData || searchCriteriaData.criteria.length === 0) {
            return DefaultRefineSearchCriteria;
        }

        return convertSearchCriteriaResponseToSearchCriteriaFilters(searchCriteriaData);
    }, [searchCriteriaData]);

    useEffect(() => {
        setRefineSearchFiltersOverrides(null);
    }, [searchFiltersData?.id]);

    useEffect(() => {
        setRefineSearchCriteriaOverrides(null);
    }, [searchCriteriaData?.criteria]);

    const refineSearchFilters = refineSearchFiltersOverrides ?? baseRefineSearchFilters;
    const refineSearchCriteria = refineSearchCriteriaOverrides ?? baseRefineSearchCriteria;

    const setRefineSearchFilters: IdentifiedListContextType['setRefineSearchFilters'] = useCallback(
        (dispatch) => {
            setRefineSearchFiltersOverrides((prev) => {
                const current = prev ?? baseRefineSearchFilters;

                return typeof dispatch === 'function' ? dispatch(current) : dispatch;
            });
        },
        [baseRefineSearchFilters],
    );

    const setRefineSearchCriteria: IdentifiedListContextType['setRefineSearchCriteria'] = useCallback(
        (dispatch) => {
            setRefineSearchCriteriaOverrides((prev) => {
                const current = prev ?? baseRefineSearchCriteria;

                return typeof dispatch === 'function' ? dispatch(current) : dispatch;
            });
        },
        [baseRefineSearchCriteria],
    );

    const isRefineSearchFiltersReady = !isAiSearching && !isSearchFiltersLoading && !!searchFiltersData;
    const isRefineSearchCriteriaReady =
        !isAiSearching && !isSearchCriteriaLoading && !!searchCriteriaData && searchCriteriaData.criteria.length > 0;

    const setOptions: IdentifiedListContextType['setOptions'] = useCallback(
        (dispatch) => {
            setPagination((p) => ({ ...p, offset: 0 }));
            return setOptionsState(dispatch);
        },
        [setOptionsState, setPagination],
    );

    return {
        pagination,
        setPagination,
        refineSearchFilters,
        setRefineSearchFilters,
        refineSearchCriteria,
        setRefineSearchCriteria,
        isRefineSearchFiltersReady,
        isRefineSearchCriteriaReady,
        options,
        setOptions,
        ...identifiedProfiles,
    };
};

export const useIdentifiedListContext = () => {
    const context = useContext(IdentifiedListContext);

    if (!context) {
        throw new Error('useIdentifiedListContext must be used inside IdentifiedListContextProvider');
    }

    return context;
};
